import { UseTournamentsProps, UseTournamentsQueryKeyProps } from './props';
import { TournamentsQueryKeys } from '../../queryKey';
import { InfiniteData } from '@tanstack/react-query';
import { useInfiniteQuery } from '@starsoft/common/services/query-client';
import { AxiosError } from 'axios';
import { GenericError } from '@/models/generic-error';
import { fetchTournamentsWithStats } from './service';
import { FetchTournamentsInputDto } from '@/services/dto/tournaments/fetch-tournaments-input.dto';
import { useMemo } from 'react';
import { PaginatedResponse } from '@starsoft/common/models';
import { TournamentWithStats } from '@/models/tournament/with-stats';
import {
  useDebounce,
  useInfiniteQueryCallbacks,
  useInfiniteQueryReset,
} from '@starsoft/common/hooks';

export function useTournaments(options?: UseTournamentsProps) {
  const { limit = 20, query: externalQuery, status } = options ?? {};
  const debouncedQuery = useDebounce(externalQuery);

  const params: FetchTournamentsInputDto = {
    query: externalQuery ?? debouncedQuery,
    status: status,
    limit,
  };

  const queryKey: UseTournamentsQueryKeyProps = [
    TournamentsQueryKeys.Primary,
    params,
  ];

  const { getNextPageParam, getPreviousPageParam } =
    useInfiniteQueryCallbacks();
  useInfiniteQueryReset(queryKey);

  const { data, isPending, fetchStatus, ...query } = useInfiniteQuery<
    PaginatedResponse<TournamentWithStats>,
    AxiosError<GenericError>,
    InfiniteData<PaginatedResponse<TournamentWithStats>>,
    UseTournamentsQueryKeyProps
  >({
    queryKey,
    queryFn: fetchTournamentsWithStats,
    initialPageParam: 1,
    getNextPageParam,
    getPreviousPageParam,
    refetchOnWindowFocus: false,
    staleTime: 5 * 1000,
  });

  const tournaments = useMemo(
    () => data?.pages?.flatMap(page => page?.data),
    [data],
  );

  return {
    ...query,
    tournaments,
    data,
    isLoading: isPending && fetchStatus !== 'idle',
  };
}
